import { isStudentUser } from "./isStudentUser";
import { isTrainerPortalUser, TRAINER_PORTAL_ROUTES } from "./trainerPortal";
import { resolveUserRoleLabel } from "./resolveUserRoleLabel";

export const WORKSPACES = {
  STAFF: "staff",
  TRAINER: "trainer",
  STUDENT: "student",
};

export const WORKSPACE_STORAGE_KEY = "ems.activeWorkspace";

export const WORKSPACE_LABELS = {
  staff: "Staff",
  trainer: "Trainer",
  student: "Student",
};

const STAFF_ROLE_TOKENS = [
  "admin",
  "owner",
  "head",
  "training manager",
  "academic coordinator",
  "counsellor",
  "counselor",
  "accountant",
];

const SHARED_PATHS = [
  "/help",
  "/settings/change-password",
  "/organization/details",
  "/organization/switcher",
  "/dashboard",
  "/",
];

function normalizeRoleName(value) {
  return String(value || "").trim().toLowerCase();
}

export function workspaceForRoleName(roleName) {
  const name = normalizeRoleName(roleName);
  if (!name) return null;
  if (name === "student") return WORKSPACES.STUDENT;
  if (name.includes("trainer")) return WORKSPACES.TRAINER;
  if (
    STAFF_ROLE_TOKENS.some((token) => name === token || name.includes(token))
  ) {
    return WORKSPACES.STAFF;
  }
  return WORKSPACES.STAFF;
}

function pushUniqueRole(list, value) {
  const text = String(value || "").trim();
  if (!text) return;
  const key = text.toLowerCase();
  if (list.some((item) => item.toLowerCase() === key)) return;
  list.push(text);
}

export function collectUserRoleNames(userData) {
  const names = [];
  if (!userData) return names;

  if (Array.isArray(userData.roles)) {
    userData.roles.forEach((role) => {
      if (typeof role === "string") {
        pushUniqueRole(names, role);
        return;
      }
      pushUniqueRole(
        names,
        role?.user_role_name || role?.role_name || role?.name
      );
    });
  }

  pushUniqueRole(names, userData.role_name);
  pushUniqueRole(names, resolveUserRoleLabel(userData));
  pushUniqueRole(
    names,
    userData?.organizationuser?.RoleAssignment?.ApplicationUserRole
      ?.user_role_name
  );
  pushUniqueRole(
    names,
    userData?.organizationuser?.role_assignment?.application_user_role
      ?.user_role_name
  );

  return names.filter((name) => name && name !== "User");
}

export function allowedWorkspaces(userData, permissionList = null) {
  if (Array.isArray(userData?.workspaces) && userData.workspaces.length) {
    const fromApi = userData.workspaces
      .map((item) => String(item || "").trim().toLowerCase())
      .filter((item) => Object.values(WORKSPACES).includes(item));
    if (fromApi.length) {
      return [...new Set(fromApi)];
    }
  }

  const found = new Set();
  collectUserRoleNames(userData).forEach((name) => {
    const workspace = workspaceForRoleName(name);
    if (workspace) found.add(workspace);
  });

  if (isStudentUser(userData, permissionList)) {
    found.add(WORKSPACES.STUDENT);
  }
  if (isTrainerPortalUser(userData, permissionList)) {
    found.add(WORKSPACES.TRAINER);
  }
  if (!found.size) {
    found.add(WORKSPACES.STAFF);
  }

  return Array.from(found);
}

export function getStoredWorkspace() {
  if (typeof window === "undefined") return null;
  const value = String(localStorage.getItem(WORKSPACE_STORAGE_KEY) || "")
    .trim()
    .toLowerCase();
  return Object.values(WORKSPACES).includes(value) ? value : null;
}

export function setStoredWorkspace(workspace) {
  if (typeof window === "undefined") return;
  const value = String(workspace || "").trim().toLowerCase();
  if (!Object.values(WORKSPACES).includes(value)) return;
  localStorage.setItem(WORKSPACE_STORAGE_KEY, value);
}

export function clearStoredWorkspace() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(WORKSPACE_STORAGE_KEY);
}

export function resolveActiveWorkspace(userData, permissionList = null) {
  const allowed = allowedWorkspaces(userData, permissionList);
  const stored = getStoredWorkspace();
  if (stored && allowed.includes(stored)) return stored;
  if (allowed.includes(WORKSPACES.STAFF)) return WORKSPACES.STAFF;
  if (allowed.includes(WORKSPACES.TRAINER)) return WORKSPACES.TRAINER;
  if (allowed.includes(WORKSPACES.STUDENT)) return WORKSPACES.STUDENT;
  return allowed[0] || WORKSPACES.STAFF;
}

export function workspaceHomePath(workspace) {
  if (workspace === WORKSPACES.STUDENT) return "/student/dashboard";
  return "/dashboard";
}

export function isStudentPath(pathname) {
  return String(pathname || "").startsWith("/student");
}

export function trainerListPath(pathname) {
  const current = String(pathname || "");
  return current.split("/").slice(0, 2).join("/") || current;
}

export function isTrainerPath(pathname) {
  const listPath = trainerListPath(pathname);
  return TRAINER_PORTAL_ROUTES.includes(listPath);
}

export function isSharedWorkspacePath(pathname) {
  const current = String(pathname || "");
  return SHARED_PATHS.some(
    (path) => current === path || current.startsWith(`${path}/`)
  );
}

export function workspaceForPath(pathname) {
  if (isStudentPath(pathname)) return WORKSPACES.STUDENT;
  if (isTrainerPath(pathname)) return WORKSPACES.TRAINER;
  if (isSharedWorkspacePath(pathname)) return null;
  return WORKSPACES.STAFF;
}

export function userHasRoleName(userData, matchers) {
  const tokens = (Array.isArray(matchers) ? matchers : [matchers]).map(
    (item) => String(item || "").trim().toLowerCase()
  );
  return collectUserRoleNames(userData).some((name) => {
    const normalized = normalizeRoleName(name);
    return tokens.some(
      (token) => normalized === token || normalized.includes(token)
    );
  });
}
